import { useMemo, useState } from 'react'
import { Pencil, Plus, Tags, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { useToast } from '@/hooks/useToast'
import { useInboxCategories, useDeleteCategory } from '@/features/inbox/queries'
import type { InboxCategory } from '@/features/inbox/types'
import { CategoryFormModal } from './CategoryFormModal'

export function CategoriesPanel({ canManage }: { canManage: boolean }) {
  const { toast } = useToast()
  const categoriesQuery = useInboxCategories()
  const deleteCategory = useDeleteCategory()
  const [editing, setEditing] = useState<InboxCategory | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<InboxCategory | null>(null)

  const categories = categoriesQuery.data?.categories ?? []

  const onDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteCategory.mutateAsync(pendingDelete.id)
      toast({ title: 'Category deleted' })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Delete failed'
      toast({ title: 'Could not delete', description: msg, variant: 'destructive' })
    } finally {
      setPendingDelete(null)
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: 'Category',
        accessorFn: (c: InboxCategory) => c.name,
        cell: ({ row }: { row: { original: InboxCategory } }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        accessorFn: (c: InboxCategory) => (c.is_system ? 'System' : 'Custom'),
        cell: ({ row }: { row: { original: InboxCategory } }) =>
          row.original.is_system ? (
            <Badge variant="secondary">System</Badge>
          ) : (
            <Badge variant="brand">Custom</Badge>
          ),
      },
      {
        id: 'financial',
        header: 'Financial',
        accessorFn: (c: InboxCategory) => (c.is_financial ? 'Yes' : 'No'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: { row: { original: InboxCategory } }) => {
          const c = row.original
          if (!canManage || c.is_system) {
            return <span className="text-xs text-muted-foreground">-</span>
          }
          return (
            <div className="flex items-center justify-end gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                title="Edit"
                onClick={() => {
                  setEditing(c)
                  setFormOpen(true)
                }}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                title="Delete"
                onClick={() => setPendingDelete(c)}
              >
                <Trash2 className="size-4 text-destructive" />
              </Button>
            </div>
          )
        },
      },
    ],
    [canManage]
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span>Categories</span>
          {canManage ? (
            <Toolbar align="end">
              <Button
                size="sm"
                onClick={() => {
                  setEditing(null)
                  setFormOpen(true)
                }}
              >
                <Plus className="size-4" />
                New category
              </Button>
            </Toolbar>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={categories}
          loading={categoriesQuery.isLoading}
          empty={
            <EmptyState
              icon={Tags}
              title="No categories"
              description="System categories always exist. Add your own to group mail your way."
            />
          }
        />
      </CardContent>

      <CategoryFormModal open={formOpen} onOpenChange={setFormOpen} category={editing} />

      <Modal.Confirm
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete category?"
        body={
          <p>
            Emails in <span className="font-medium">{pendingDelete?.name}</span> fall back to
            their system category. This cannot be undone.
          </p>
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={onDelete}
        busy={deleteCategory.isPending}
      />
    </Card>
  )
}
